import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Link2, Users, Calendar, Trash2, Eye, Download, Copy, ExternalLink, ToggleLeft, ToggleRight } from 'lucide-react';

export default function AttendancePage() {
  const { api, isAdmin } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventRecords, setEventRecords] = useState([]);
  const [createForm, setCreateForm] = useState({ name: '', description: '', event_date: '' });
  const [createLoading, setCreateLoading] = useState(false);

  const baseUrl = window.location.origin;

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await api.get('/attendance/events');
      setEvents(response.data.events || []);
    } catch (error) {
      toast.error('Failed to fetch attendance events');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    if (!createForm.name.trim()) {
      toast.error('Please enter event name');
      return;
    }

    setCreateLoading(true);
    try {
      const response = await api.post('/attendance/events', createForm);
      toast.success('Attendance event created!');
      setShowCreateDialog(false);
      setCreateForm({ name: '', description: '', event_date: '' });
      fetchEvents();
      
      // Show the public link
      const publicLink = `${baseUrl}/attendance/${response.data.public_code}`;
      toast.success(
        <div>
          <p className="font-medium">Public Link Created!</p>
          <p className="text-xs mt-1 break-all">{publicLink}</p>
        </div>,
        { duration: 10000 }
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleViewDetails = async (event) => {
    try {
      const response = await api.get(`/attendance/events/${event.id}`);
      setSelectedEvent(response.data.event);
      setEventRecords(response.data.records || []);
      setShowDetailsDialog(true);
    } catch (error) {
      toast.error('Failed to fetch event details');
    }
  };

  const handleToggleEvent = async (event) => {
    try {
      const response = await api.put(`/attendance/events/${event.id}/toggle`);
      toast.success(response.data.message);
      fetchEvents();
    } catch (error) {
      toast.error('Failed to toggle event status');
    }
  };

  const handleDeleteEvent = async (event) => {
    if (!window.confirm(`Delete "${event.name}"? This will remove all attendance records.`)) return;
    
    try {
      await api.delete(`/attendance/events/${event.id}`);
      toast.success('Event deleted');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleExport = async (eventId, format) => {
    try {
      const response = await api.get(`/attendance/events/${eventId}/export?format=${format}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attendance.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${format.toUpperCase()} exported`);
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied to clipboard!');
  };

  const getPublicLink = (event) => `${baseUrl}/attendance/${event.public_code}`;

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="attendance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-semibold text-slate-800">Attendance</h1>
          <p className="text-xs sm:text-sm text-slate-500">{events.length} attendance events</p>
        </div>
        
        <Button 
          onClick={() => setShowCreateDialog(true)} 
          className="bg-navy hover:bg-navy/90"
          data-testid="create-event-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Event
        </Button>
      </div>

      {/* Events Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-navy border-t-transparent rounded-full animate-spin" />
          </div>
        ) : events.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No attendance events yet</p>
            <p className="text-sm text-slate-400">Create your first event to start tracking attendance</p>
          </div>
        ) : (
          events.map((event) => (
            <Card key={event.id} className="border-0 shadow-card hover:shadow-lg transition-shadow" data-testid={`event-card-${event.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base font-semibold truncate">{event.name}</CardTitle>
                    <p className="text-xs text-slate-500 mt-1">{event.event_date}</p>
                  </div>
                  <Badge className={event.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}>
                    {event.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Stats */}
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1.5 text-slate-600">
                    <Users className="w-4 h-4" />
                    <span className="font-medium">{event.total_attendance || 0}</span>
                    <span className="text-slate-400">attended</span>
                  </div>
                </div>
                
                {/* Public Link */}
                <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                  <Link2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <span className="text-xs text-slate-500 truncate flex-1">{getPublicLink(event)}</span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 w-6 p-0"
                    onClick={() => copyToClipboard(getPublicLink(event))}
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 w-6 p-0"
                    onClick={() => window.open(getPublicLink(event), '_blank')}
                  >
                    <ExternalLink className="w-3 h-3" />
                  </Button>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-2 pt-2 border-t">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1 text-xs"
                    onClick={() => handleViewDetails(event)}
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    View
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleToggleEvent(event)}
                    className={event.is_active ? 'text-amber-600' : 'text-green-600'}
                  >
                    {event.is_active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-red-500 hover:bg-red-50"
                    onClick={() => handleDeleteEvent(event)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Event Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-navy" />
              Create Attendance Event
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateEvent} className="space-y-4">
            <div>
              <Label htmlFor="eventName">Event Name *</Label>
              <Input
                id="eventName"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="e.g., Friday Majlis"
                required
                className="mt-1"
                data-testid="event-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="eventDate">Event Date</Label>
              <Input
                id="eventDate"
                type="date"
                value={createForm.event_date}
                onChange={(e) => setCreateForm({ ...createForm, event_date: e.target.value })}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="eventDesc">Description (Optional)</Label>
              <Input
                id="eventDesc"
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                placeholder="Brief description"
                className="mt-1"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-navy" disabled={createLoading}>
                {createLoading ? 'Creating...' : 'Create Event'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Event Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-navy" />
              {selectedEvent?.name}
              <Badge className="ml-2">{eventRecords.length} attended</Badge>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto">
            {/* Export Buttons */}
            <div className="flex gap-2 mb-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleExport(selectedEvent?.id, 'excel')}
              >
                <Download className="w-4 h-4 mr-1" />
                Excel
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleExport(selectedEvent?.id, 'pdf')}
              >
                <Download className="w-4 h-4 mr-1" />
                PDF
              </Button>
            </div>
            
            {/* Records Table */}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>ITS ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden sm:table-cell">Mobile</TableHead>
                    <TableHead className="hidden md:table-cell">Mohalla</TableHead>
                    <TableHead>Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {eventRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        No attendance records yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    eventRecords.map((record, idx) => (
                      <TableRow key={record.id}>
                        <TableCell className="font-mono text-xs">{idx + 1}</TableCell>
                        <TableCell className="font-mono text-emerald-600">{record.its_id}</TableCell>
                        <TableCell className="font-medium">{record.member_name}</TableCell>
                        <TableCell className="hidden sm:table-cell text-slate-500">{record.mobile}</TableCell>
                        <TableCell className="hidden md:table-cell text-slate-500">{record.mohalla}</TableCell>
                        <TableCell className="text-xs text-slate-400">
                          {new Date(record.marked_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
